import { useState, useEffect } from 'react'
import { useLatest } from '../useLatest'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { merkeDetaljerUrl, modellDetaljerUrl } from '../detaljerUrl'
import SubTabBar from '../SubTabBar'
import { useBruktbilAvansert } from '../tilgang'
import {
  fetchMerkeVolume,
  fetchModellVolume,
  fetchPerUke,
  fetchSammenligning,
} from './api'
import type {
  MerkeVolumeResponse,
  ModellRow,
  ModellVolumeResponse,
  PerUkeResponse,
  SammenligningResponse,
} from './api'
import MerkeTable from './MerkeTable'
import ModellTable from './ModellTable'
import FilterBar from './FilterBar'
import PerUkeChart from './PerUkeChart'
import SammenligningGrid from './SammenligningGrid'
import QueryExplanation from './QueryExplanation'
import OversiktExplanation from './OversiktExplanation'

type TabSlug = 'merker' | 'modeller' | 'sammenligning'

// Cars first registered before 2000 are not relevant — the Sammenligning
// queries always apply this floor, even before the user touches the slider.
const AAR_MIN_FLOOR = 2000

// Discovery tables — pagination + server-side search (#476), same page size
// as before so an unfiltered first load looks identical to pre-#476.
const OVERSIKT_PAGE_SIZE = 50
const DEBOUNCE_MS = 300

const TABS: { slug: TabSlug; labelKey: `merkeModell.tabs.${TabSlug}` }[] = [
  { slug: 'merker', labelKey: 'merkeModell.tabs.merker' },
  { slug: 'modeller', labelKey: 'merkeModell.tabs.modeller' },
  { slug: 'sammenligning', labelKey: 'merkeModell.tabs.sammenligning' },
]

// Merker and Modeller used to share one "Oversikt" tab as two grids side by
// side (#489 split them). Old links and bookmarks still carry `?tab=oversikt`
// — land them on Merker rather than on an unknown tab.
function resolveTab(raw: string | undefined): TabSlug {
  if (raw === 'modeller' || raw === 'sammenligning') return raw
  return 'merker'
}

export default function MerkeModellPage() {
  const { t } = useTranslation('bruktbil')
  const navigate = useNavigate()
  // "Se detaljer" opens the per-forhandler share page — advanced only (#673).
  const avansert = useBruktbilAvansert()
  // Kept whole as well as destructured: the detaljer links need the postnummer
  // toggle state, which is not part of the API-call filter object below.
  const bruktbilFilters = useBruktbilFilters()
  const {
    from, to, klasse, varebil, omregistrering, tab, merke, aarMin, aarMax,
    selgerPostnummer, kjoperPostnummer, setFilters,
  } = bruktbilFilters
  const activeTab = resolveTab(tab)

  const [merkeData, setMerkeData] = useState<MerkeVolumeResponse | null>(null)
  const [modellData, setModellData] = useState<ModellVolumeResponse | null>(null)
  const [perUkeData, setPerUkeData] = useState<PerUkeResponse | null>(null)
  const [sammenligningData, setSammenligningData] = useState<SammenligningResponse | null>(null)
  // Merke and Modell fetch independently (#476) — paginating/searching one
  // must not wait on or refetch the other. Since #489 they also live on
  // separate tabs, so only the visible one fetches at all.
  const [merkeLoading, setMerkeLoading] = useState(false)
  const [modellLoading, setModellLoading] = useState(false)
  const [sammenligningLoading, setSammenligningLoading] = useState(false)

  // Independently paginated + searched (#476): merke and modell are two
  // separate paginated lists, each with its own page/search.
  const [merkePage, setMerkePage] = useState(1)
  const [modellPage, setModellPage] = useState(1)
  const [merkeSearchInput, setMerkeSearchInput] = useState('')
  const [modellSearchInput, setModellSearchInput] = useState('')
  const [merkeQ, setMerkeQ] = useState('') // debounced value actually sent
  const [modellQ, setModellQ] = useState('')

  // Mine markedsområder reaches this page too (#489). Every merke-modell route
  // already resolves selger_postnummer / kjoper_postnummer into GlobalFilters
  // and every query goes through build_base_query — the page simply never sent
  // the parameters, so the toggle sat visible in the layout and did nothing.
  const filters = {
    from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer,
  }
  const hasSelection = merke.length > 0

  useEffect(() => {
    const timer = setTimeout(() => setMerkeQ(merkeSearchInput), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [merkeSearchInput])

  useEffect(() => {
    const timer = setTimeout(() => setModellQ(modellSearchInput), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [modellSearchInput])

  // A new search or a changed global filter invalidates the current page —
  // reset to 1 so a stale page number doesn't request a page past the end
  // of a now-smaller (or reordered) result set.
  useEffect(() => {
    setMerkePage(1)
  }, [from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer, merkeQ])
  // The merke drill-down narrows the Modell list the same way a search does,
  // so it invalidates the page number for the same reason (#489).
  useEffect(() => {
    setModellPage(1)
  }, [from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer, modellQ, JSON.stringify(merke)])

  // Merker — discovery table under the global filters only. Independent of the
  // Modell fetch below: its own page/search must not trigger or wait on a
  // Modell refetch (see #476 — was previously one combined Promise.all).
  useLatest(alive => {
    if (activeTab !== 'merker') return
    setMerkeLoading(true)
    fetchMerkeVolume(filters, OVERSIKT_PAGE_SIZE, (merkePage - 1) * OVERSIKT_PAGE_SIZE, merkeQ)
      .then(r => { if (alive()) setMerkeData(r.data) })
      .finally(() => { if (alive()) setMerkeLoading(false) })
  }, [from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer, activeTab, merkePage, merkeQ])

  // Modeller — same discovery table for models, scoped to the selected merker
  // when the user drilled in from Merker (#489). Empty selection = all models,
  // which is what the tab shows when opened directly.
  useLatest(alive => {
    if (activeTab !== 'modeller') return
    setModellLoading(true)
    fetchModellVolume(filters, merke, OVERSIKT_PAGE_SIZE, (modellPage - 1) * OVERSIKT_PAGE_SIZE, modellQ)
      .then(r => { if (alive()) setModellData(r.data) })
      .finally(() => { if (alive()) setModellLoading(false) })
  }, [from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer, activeTab, modellPage, modellQ, JSON.stringify(merke)])

  // Sammenligning — per-uke chart + comparison grid for the current selection.
  // Series dimension is merke × klasse; nothing is fetched until at least one
  // merke is picked.
  useLatest(alive => {
    if (activeTab !== 'sammenligning') return
    if (!hasSelection) {
      setPerUkeData(null)
      setSammenligningData(null)
      return
    }
    setSammenligningLoading(true)
    const sel = { merke, aarMin: aarMin ?? AAR_MIN_FLOOR, aarMax }
    Promise.all([fetchPerUke(filters, sel), fetchSammenligning(filters, sel)])
      .then(([pRes, sRes]) => {
        if (!alive()) return
        setPerUkeData(pRes.data)
        setSammenligningData(sRes.data)
      })
      .finally(() => { if (alive()) setSammenligningLoading(false) })
  }, [
    from, to, JSON.stringify(klasse), varebil, omregistrering, activeTab,
    selgerPostnummer, kjoperPostnummer, JSON.stringify(merke), aarMin, aarMax,
  ])

  // Drill-down (#489): a Merker row opens that merke's models. It *replaces*
  // the selection rather than adding to it — accumulating merker across clicks
  // would turn a drill-down into a multi-select nobody asked for. The chip
  // above the Modell table shows what is applied and takes it off again.
  function handleMerkeRowClick(clicked: string) {
    setFilters({ merke: [clicked], tab: 'modeller' })
  }

  // Navigation lives here, not in the tables: they are presentational and take
  // callbacks for everything else too (#489 AC5/AC6).
  function openMerkeDetaljer(clicked: string) {
    navigate(merkeDetaljerUrl(clicked, bruktbilFilters))
  }

  function openModellDetaljer(row: ModellRow) {
    if (row.merke && row.modell) navigate(modellDetaljerUrl(row.merke, row.modell, bruktbilFilters))
  }

  function removeMerkeFilter(name: string) {
    setFilters({ merke: merke.filter(m => m !== name) })
  }

  const exportMeta = {
    title: t('merkeModell.sammenligningExportTitle'),
    dateRange: `${from} – ${to}`,
    filters: [
      t('export.merker', { list: merke.length ? merke.join(', ') : '—' }),
      klasse.length ? t('export.klasserValgt', { n: klasse.length }) : t('export.klasserAlle'),
      aarMax
        ? t('export.forsteGangsAarFraTil', { from: aarMin ?? AAR_MIN_FLOOR, to: aarMax })
        : t('export.forsteGangsAarFra', { from: aarMin ?? AAR_MIN_FLOOR }),
      varebil ? t('export.kjoretoyAlle') : t('export.kjoretoyPersonbil'),
    ],
  }

  // Discovery tables run on the global filters only (no år-range).
  const oversiktExportMeta = {
    dateRange: `${from} – ${to}`,
    filters: [
      klasse.length ? t('export.klasserValgt', { n: klasse.length }) : t('export.klasserAlle'),
      varebil ? t('export.kjoretoyAlle') : t('export.kjoretoyPersonbil'),
    ],
  }

  // The Modell table can be scoped to a merke (#489) — an export that omits
  // the drill-down would misrepresent itself as the whole market.
  const modellExportMeta = {
    ...oversiktExportMeta,
    filters: hasSelection
      ? [t('export.merker', { list: merke.join(', ') }), ...oversiktExportMeta.filters]
      : oversiktExportMeta.filters,
  }

  return (
    <div className="space-y-3">
      <SubTabBar
        parent={t('layout.tabs.merkeModell')}
        tabs={TABS.map(tab => ({ key: tab.slug, label: t(tab.labelKey) }))}
        active={activeTab}
        onChange={slug => setFilters({ tab: slug })}
        testIdPrefix="merke-modell-tab-"
      />

      {/* Merker and Modeller each show their own skeleton only before their
       *  own first load, and stay mounted (search box included) through every
       *  later refetch so a zero-match search never hides the box the user
       *  needs to clear it (#476). */}
      {activeTab === 'merker' && (
        <>
          <OversiktExplanation from={from} to={to} varebil={varebil} klasse={klasse} />
          {merkeLoading && !merkeData && (
            <div className="card p-4 animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
          )}
          {merkeData && (
            <MerkeTable
              rows={merkeData.rows}
              onRowClick={handleMerkeRowClick}
              onDetaljer={avansert ? openMerkeDetaljer : undefined}
              exportMeta={oversiktExportMeta}
              page={merkePage}
              pages={Math.max(1, Math.ceil(merkeData.total / OVERSIKT_PAGE_SIZE))}
              total={merkeData.total}
              onPageChange={setMerkePage}
              search={merkeSearchInput}
              onSearchChange={setMerkeSearchInput}
              startRank={(merkePage - 1) * OVERSIKT_PAGE_SIZE + 1}
            />
          )}
        </>
      )}

      {activeTab === 'modeller' && (
        <>
          <OversiktExplanation from={from} to={to} varebil={varebil} klasse={klasse} />
          {hasSelection && (
            <div
              data-testid="modell-merke-filter"
              className="card p-3 flex flex-wrap items-center gap-2"
            >
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('merkeModell.filtrertPaMerke')}</span>
              {merke.map(m => (
                <span
                  key={m}
                  className="inline-flex items-center gap-1 rounded-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2 py-0.5"
                >
                  {m}
                  <button
                    type="button"
                    data-testid={`modell-remove-merke-${m}`}
                    onClick={() => removeMerkeFilter(m)}
                    className="text-primary-500 hover:text-primary-700"
                    aria-label={t('merkeMultiSelect.fjern', { name: m })}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          {modellLoading && !modellData && (
            <div className="card p-4 animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
          )}
          {modellData && (
            <ModellTable
              rows={modellData.rows}
              selectedMerke={merke.length === 1 ? merke[0] : null}
              onDetaljer={avansert ? openModellDetaljer : undefined}
              exportMeta={modellExportMeta}
              page={modellPage}
              pages={Math.max(1, Math.ceil(modellData.total / OVERSIKT_PAGE_SIZE))}
              total={modellData.total}
              onPageChange={setModellPage}
              search={modellSearchInput}
              onSearchChange={setModellSearchInput}
              startRank={(modellPage - 1) * OVERSIKT_PAGE_SIZE + 1}
            />
          )}
        </>
      )}

      {activeTab === 'sammenligning' && (
        <>
          <FilterBar
            filters={filters}
            merke={merke}
            aarMin={aarMin}
            aarMax={aarMax}
            onChange={partial => setFilters(partial)}
          />
          <QueryExplanation
            from={from}
            to={to}
            varebil={varebil}
            klasse={klasse}
            merke={merke}
            aarMin={aarMin}
            aarMax={aarMax}
          />
          {hasSelection ? (
            <>
              <PerUkeChart data={perUkeData} loading={sammenligningLoading} />
              <SammenligningGrid
                rows={sammenligningData?.rows ?? []}
                exportMeta={exportMeta}
              />
            </>
          ) : (
            <div
              data-testid="sammenligning-prompt"
              className="card p-4 text-sm text-gray-500"
            >
              {t('merkeModell.velgMerkePrompt')}
            </div>
          )}
        </>
      )}
    </div>
  )
}
